import { useState } from "react";
import { ArrowDown, ArrowUp, GripVertical, Plus, Trash2 } from "lucide-react";
import { api, ApiError } from "./api";
import type { Task } from "./types";
import { Button, err } from "./shared";
import { tr } from "./localization";

export type ChecklistItem = {
  id: string;
  text: string;
  position: number;
  is_completed: boolean;
  completed_by_id: string | null;
  completed_at: string | null;
};

export function Checklist({
  task,
  refresh,
}: {
  task: Task;
  refresh: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [dragged, setDragged] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const items = task.checklist || [];
  const edit = task.permissions.checklist_edit;
  const toggle = task.permissions.checklist_toggle;
  async function change(data: Record<string, unknown>) {
    if (busy) return false;
    setBusy(true);
    try {
      await api(`/tasks/${task.id}/checklist`, "POST", {
        version: task.version,
        ...data,
      });
      refresh();
      return true;
    } catch (e) {
      err(e);
      if (e instanceof ApiError && e.status === 409) refresh();
      return false;
    } finally {
      setBusy(false);
    }
  }
  function move(id: string, index: number) {
    const ids = items.map((i) => i.id).filter((i) => i !== id);
    ids.splice(index, 0, id);
    void change({ operation: "reorder", item_ids: ids });
  }
  if (!items.length && !adding)
    return edit ? (
      <Button className="checklist-add" onClick={() => setAdding(true)}>
        <Plus size={16} />
        {tr("Добавить чек-лист")}
      </Button>
    ) : null;
  return (
    <section className="task-checklist" aria-label={tr("Чек-лист")}>
      <div className="checklist-heading">
        <strong>{tr("Чек-лист")}</strong>
        <span>
          {task.checklist_completed || 0} {tr("из")} {items.length}
        </span>
      </div>
      {!!items.length && (
        <progress
          max={items.length}
          value={task.checklist_completed || 0}
          aria-label={tr("Выполнено пунктов")}
        />
      )}
      <ul>
        {items.map((item, index) => (
          <li
            key={item.id}
            className={item.is_completed ? "is-completed" : ""}
            onDragOver={(e) => {
              if (edit && dragged) e.preventDefault();
            }}
            onDrop={(e) => {
              e.preventDefault();
              if (dragged && dragged !== item.id) move(dragged, index);
              setDragged(null);
            }}
          >
            {edit && (
              <span
                draggable={!busy}
                className="checklist-grip"
                onDragStart={() => setDragged(item.id)}
                onDragEnd={() => setDragged(null)}
              >
                <GripVertical size={16} />
              </span>
            )}
            <input
              type="checkbox"
              checked={item.is_completed}
              disabled={!toggle || busy}
              aria-label={`${tr("Выполнено")}: ${item.text}`}
              onChange={(e) =>
                void change({
                  operation: "toggle",
                  item_id: item.id,
                  completed: e.target.checked,
                })
              }
            />
            {edit ? (
              <input
                className="checklist-text"
                aria-label={tr("Текст пункта")}
                maxLength={500}
                disabled={busy}
                value={drafts[item.id] ?? item.text}
                onChange={(e) =>
                  setDrafts((v) => ({ ...v, [item.id]: e.target.value }))
                }
                onBlur={async () => {
                  const value = drafts[item.id];
                  if (
                    value !== undefined &&
                    value !== item.text &&
                    (await change({
                      operation: "rename",
                      item_id: item.id,
                      text: value,
                    }))
                  )
                    setDrafts((v) => {
                      const next = { ...v };
                      delete next[item.id];
                      return next;
                    });
                }}
              />
            ) : (
              <span className="checklist-text">{item.text}</span>
            )}
            {edit && (
              <div className="checklist-controls">
                <Button
                  className="icon"
                  title={tr("Выше")}
                  aria-label={tr("Переместить выше")}
                  disabled={busy || index === 0}
                  onClick={() => move(item.id, index - 1)}
                >
                  <ArrowUp size={14} />
                </Button>
                <Button
                  className="icon"
                  title={tr("Ниже")}
                  aria-label={tr("Переместить ниже")}
                  disabled={busy || index === items.length - 1}
                  onClick={() => move(item.id, index + 1)}
                >
                  <ArrowDown size={14} />
                </Button>
                <Button
                  className="icon"
                  title={tr("Удалить пункт")}
                  aria-label={tr("Удалить пункт")}
                  disabled={busy}
                  onClick={() =>
                    void change({ operation: "delete", item_id: item.id })
                  }
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
      {edit && (
        <form
          className="checklist-new"
          onSubmit={async (e) => {
            e.preventDefault();
            if (await change({ operation: "add", text })) setText("");
          }}
        >
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={tr("Новый пункт")}
            aria-label={tr("Новый пункт")}
            maxLength={500}
            required
            disabled={busy}
          />
          <Button type="submit" disabled={busy || !text.trim()}>
            <Plus size={16} />
            {tr("Добавить")}
          </Button>
        </form>
      )}
    </section>
  );
}

export function ChecklistProgress({ task }: { task: Task }) {
  return task.checklist_total ? (
    <span
      className="checklist-summary"
      title={tr("Выполнено пунктов чек-листа")}
    >
      ☑ {task.checklist_completed || 0}/{task.checklist_total}
    </span>
  ) : null;
}
